import { useUser } from "@clerk/clerk-react";
import { AlertCircle, Loader2, Pencil } from "lucide-react";
import { useState } from "react";
import { api } from "@/services/api";

const FIELD_CLASS =
  "w-full rounded-[9px] border border-content-border bg-card px-3.5 py-2.5 text-[14px] text-card-foreground outline-none focus:border-brand/60";

/** Name row, editable in place.
 *
 * Clerk is the source of truth — the `user.updated` webhook overwrites both
 * fields on the profile from Clerk whenever anything changes there — so the
 * write goes to Clerk first. Our own profile is then updated directly rather
 * than waiting on webhook delivery, because the certificate run reads
 * `user_profiles` and excludes anyone whose name is incomplete.
 */
export function NameRow() {
  const { user } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const isIncomplete = !user?.firstName?.trim() || !user?.lastName?.trim();

  const startEditing = () => {
    setFirst(user?.firstName ?? "");
    setLast(user?.lastName ?? "");
    setError(null);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const firstName = first.trim();
    const lastName = last.trim();
    if (!firstName || !lastName) {
      setError("Enter both a first and a last name.");
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      await user?.update({ firstName, lastName });
      await api.updateMyIdentity({ first_name: firstName, last_name: lastName });
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your name.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <div className="grid grid-cols-[110px_1fr] items-center gap-4 px-7 py-5 sm:grid-cols-[170px_1fr]">
        <div className="text-[13px] font-bold text-[#5C6E63]">Full name</div>
        <div className="flex min-w-0 items-center gap-4">
          <div className="min-w-0 flex-1">
            <div
              className={`text-[15px] font-semibold ${fullName ? "text-card-foreground" : "text-content-muted"}`}
            >
              {fullName || "Not set"}
            </div>
            {isIncomplete && (
              <div className="mt-1.5 flex items-center gap-1.5 text-[12.5px] text-[#8A6D1F]">
                <AlertCircle className="h-3.5 w-3.5 flex-none" strokeWidth={2} />
                Add your full name to receive your certificate
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={startEditing}
            className="flex flex-none items-center gap-1.5 rounded-[9px] border border-content-border px-3.5 py-2 text-[13px] font-bold text-card-foreground transition-colors hover:bg-content-subtle"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
            Edit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[110px_1fr] gap-4 px-7 py-5 sm:grid-cols-[170px_1fr]">
      <div className="pt-2.5 text-[13px] font-bold text-[#5C6E63]">Full name</div>
      <div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="account-first-name"
              className="mb-1.5 block text-[11px] font-bold uppercase tracking-[.1em] text-content-muted"
            >
              First name
            </label>
            <input
              id="account-first-name"
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <label
              htmlFor="account-last-name"
              className="mb-1.5 block text-[11px] font-bold uppercase tracking-[.1em] text-content-muted"
            >
              Last name
            </label>
            <input
              id="account-last-name"
              value={last}
              onChange={(e) => setLast(e.target.value)}
              className={FIELD_CLASS}
            />
          </div>
        </div>
        <p className="mt-2.5 text-[12.5px] text-content-muted">
          This is the name printed on your certificate.
        </p>
        {error && <p className="mt-2 text-[13px] font-semibold text-red-700">{error}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 rounded-[10px] bg-[#013220] px-5 py-2.5 text-[13px] font-bold text-[#F2EFE3] transition-colors hover:bg-[#0A4530] disabled:opacity-60"
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save name
          </button>
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            disabled={isSaving}
            className="text-[13px] font-bold text-content-muted hover:underline disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
